import { Link } from "@inertiajs/react"
import { AdminLayout } from "@/components/layouts/admin-layout"
import { Button } from "@/components/ui/button"
import { MainContainer } from "@/components/ui/container"
import { DataTable } from "@/components/ui/data-table"
import type {
  AppliedFilters,
  AppliedSort,
  DataColumn,
  FilterOption,
} from "@/components/ui/data-table"
import { PageHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import type { TransactionRow } from "@/features/admin/transactions"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"
import type { StatusTone } from "@/lib/status-colors"

type Pagination = {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
  previousPage: number | null
  nextPage: number | null
}

type Props = {
  transactions: TransactionRow[]
  pagination: Pagination
  search: string
  sort: AppliedSort
  filters: AppliedFilters
  statusOptions: FilterOption[]
  paymentMethodOptions: FilterOption[]
}

const STATUS_TONES: Record<string, StatusTone> = {
  succeeded: "success",
  failed: "danger",
  canceled: "disabled",
  pending: "reserved",
  processing: "reserved",
}

const TABLE_PROPS = [ "transactions", "pagination", "search", "sort", "filters" ]

export default function AdminTransactionsIndex({
  transactions,
  pagination,
  search,
  sort,
  filters,
  statusOptions,
  paymentMethodOptions,
}: Props) {
  const columns: DataColumn<TransactionRow>[] = [
    {
      accessorKey: "createdAt",
      header: t("ui.react.pages.admin.transactions.index.created_at"),
      // Platform time, not the restaurant's. This screen crosses tenants — a
      // list where every row is stamped in its own zone cannot be read in
      // order, which is the one thing an operator needs from it.
      field: { type: "datetime" },
      sortKey: "created_at",
      filter: { kind: "dateRange" },
      filterKey: "created_at",
      mobile: "meta",
    },
    {
      id: "location",
      accessorFn: (row) => row.location.name,
      header: t("ui.react.pages.admin.transactions.index.location"),
      field: { type: "title", truncate: true },
    },
    {
      id: "diner",
      header: t("ui.react.pages.admin.transactions.index.diner"),
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate text-sm">{row.original.diner.name}</p>
          <p className="text-muted-foreground truncate text-xs">{row.original.diner.email}</p>
        </div>
      ),
    },
    {
      accessorKey: "totalAmount",
      header: t("ui.react.pages.admin.transactions.index.amount"),
      field: { type: "currency" },
      sortKey: "total_amount",
      mobile: "secondary",
    },
    {
      accessorKey: "paymentMethodLabel",
      header: t("ui.react.pages.admin.transactions.index.payment_method"),
      field: { type: "text" },
      sortKey: "payment_method",
      // The column shows the localized label; the server whitelist knows the
      // stored key, so the filter names that one.
      filterKey: "payment_method",
      filter: { kind: "enum", options: paymentMethodOptions },
    },
    {
      accessorKey: "status",
      header: t("ui.react.pages.admin.transactions.index.status"),
      field: { type: "status", labelKey: "statusLabel", tones: STATUS_TONES },
      sortKey: "status",
      filterKey: "status",
      filter: { kind: "enum", options: statusOptions },
      mobile: "meta",
    },
    {
      // Null means the attempt never reached Stripe, which is a different
      // failure from one Stripe refused. Saying so beats an empty cell.
      accessorKey: "providerStatus",
      header: t("ui.react.pages.admin.transactions.index.provider"),
      cell: ({ row }) => (
        <span className="text-muted-foreground text-xs">
          {row.original.providerStatus ?? t("ui.react.pages.admin.transactions.index.no_provider")}
        </span>
      ),
    },
    {
      id: "actions",
      header: t("ui.react.pages.admin.transactions.index.actions"),
      enableHiding: false,
      field: {
        type: "actions",
        render: (transaction) => (
          <Button asChild variant="outline" size="sm">
            <Link
              href={`/transactions/${transaction.id}`}
              data-testid={`transaction-open-${transaction.id}`}
            >
              {t("ui.react.pages.admin.transactions.index.open")}
            </Link>
          </Button>
        ),
      },
    },
  ]

  return (
    <AdminLayout>
      <MainContainer size="fullWidth">
        <PageHeader
          title={t("ui.react.pages.admin.transactions.index.title")}
          description={t("ui.react.pages.admin.transactions.index.showing", {
            count: transactions.length,
            total: pagination.totalCount,
          })}
        />

        <DataTable
          mode="server"
          url="/transactions"
          columns={columns}
          data={transactions}
          surface="card"
          search={search}
          sort={sort}
          filters={filters}
          pagination={pagination}
          reloadOnly={TABLE_PROPS}
          searchPlaceholder={t("ui.react.pages.admin.transactions.index.search")}
          // Kept hand-written: the whole card is one tap target to the detail
          // screen, which the generated feed has no way to express.
          renderMobileCard={(transaction) => (
            <Link
              href={`/transactions/${transaction.id}`}
              className="block px-4 py-3"
              data-testid={`transaction-card-${transaction.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium">{transaction.location.name}</span>
                <span className="text-foreground shrink-0 font-semibold tabular-nums">
                  {formatMoney(transaction.totalAmount)}
                </span>
              </div>
              <p className="text-muted-foreground mt-1 truncate text-sm">
                {transaction.diner.email}
              </p>
              <p className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                <StatusBadge
                  tone={STATUS_TONES[transaction.status] ?? "disabled"}
                  label={transaction.statusLabel}
                />
                <span className="tabular-nums">{formatDateTime(transaction.createdAt)}</span>
              </p>
              {transaction.failureReason ? (
                <p className="text-destructive mt-1 text-xs">{transaction.failureReason}</p>
              ) : null}
            </Link>
          )}
        />
      </MainContainer>
    </AdminLayout>
  )
}
